
import React, { useState, useMemo } from 'react';
import Header from '@/components/layout/Header';
import { OddsFilter } from '@/components/odds-analysis/OddsFilter';
import { OddsAnalysisContent } from '@/components/odds-analysis/OddsAnalysisContent';
import { useFootballFilters } from '@/hooks/useFootballFilters';
import { useFootballStates } from '@/hooks/useFootballStates';
import { useOddsAnalysisData } from '@/hooks/useOddsAnalysisData';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const OddsAnalysis: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<'home' | 'away'>('home');
  const [teamLocation, setTeamLocation] = useState<'any' | 'home' | 'away'>('any');

  const { savedStates, isLoading: isLoadingStates } = useFootballStates();

  const {
    availableCountries,
    availableLeagues,
    availableYears,
  } = useFootballFilters(savedStates || [], selectedCountry, selectedLeague, null);

  const {
    homeOddsAnalysisData,
    awayOddsAnalysisData,
    matches,
    isLoading,
    error,
    warning,
    availableTeams
  } = useOddsAnalysisData(selectedCountry, selectedLeague, selectedYears, selectedTeam, teamLocation);

  // Memoize handlers to prevent unnecessary re-renders
  const handleCountrySelect = useMemo(() => (country: string) => {
    setSelectedCountry(country);
    setSelectedLeague(null);
    setSelectedYears([]);
    setSelectedTeam(null);
  }, []);

  const handleLeagueSelect = useMemo(() => (league: string) => {
    setSelectedLeague(league);
    setSelectedYears([]);
    setSelectedTeam(null);
  }, []);

  const handleYearSelect = useMemo(() => (year: string) => {
    setSelectedYears([year]);
  }, []);

  const handleYearToggle = useMemo(() => (year: string) => {
    setSelectedYears(prev => {
      if (prev.includes(year)) {
        if (prev.length === 1) return prev;
        return prev.filter(y => y !== year);
      } else {
        return [...prev, year].sort((a, b) => b.localeCompare(a));
      }
    });
  }, []);

  const handleClearYears = useMemo(() => () => {
    if (availableYears.length > 0) {
      setSelectedYears([availableYears[0]]);
    } else {
      setSelectedYears([]);
    }
  }, [availableYears]);

  const handleTeamSelect = useMemo(() => (team: string) => {
    setSelectedTeam(team);
    setTeamLocation('any');
  }, []);

  const handleTypeChange = useMemo(() => (type: 'home' | 'away') => {
    setSelectedType(type);
  }, []);

  const handleTeamLocationChange = useMemo(() => (location: 'any' | 'home' | 'away') => {
    setTeamLocation(location);
  }, []);

  // Create a title that includes the team if selected
  const analysisTitle = useMemo(() => selectedTeam 
    ? `Betting Odds Analysis${selectedLeague ? ` - ${selectedLeague}` : ''} - ${selectedTeam}`
    : "Betting Odds Analysis", 
  [selectedTeam, selectedLeague]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-2 py-2 sm:px-4 sm:py-4">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">{analysisTitle}</h1>
        
        <div className="bg-white rounded-lg shadow p-2 sm:p-4 mb-4">
          <OddsFilter
            countries={availableCountries}
            leagues={availableLeagues}
            years={availableYears}
            teams={availableTeams}
            selectedCountry={selectedCountry}
            selectedLeague={selectedLeague}
            selectedYears={selectedYears}
            selectedTeam={selectedTeam}
            onCountrySelect={handleCountrySelect}
            onLeagueSelect={handleLeagueSelect}
            onYearSelect={handleYearSelect}
            onYearToggle={handleYearToggle}
            onClearYears={handleClearYears}
            onTeamSelect={handleTeamSelect}
          />
        </div>

        {error && (
          <Alert variant="destructive" className="mb-3 sm:mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        )}

        <OddsAnalysisContent
          homeOddsAnalysisData={homeOddsAnalysisData}
          awayOddsAnalysisData={awayOddsAnalysisData}
          matches={matches}
          isLoading={isLoading || isLoadingStates}
          error={error}
          warning={warning}
          selectedYears={selectedYears}
          selectedTeam={selectedTeam}
          selectedType={selectedType}
          teamLocation={teamLocation}
          onTypeChange={handleTypeChange}
          onTeamLocationChange={handleTeamLocationChange}
        />
      </div>
    </div>
  );
};

export default OddsAnalysis;
